import ListFilters from "@/common/components/ListFilters";
import ListHeader from "@/common/components/ListHeader";
import Table from "@/common/components/Table";
import TextField from "@/common/components/TextField";
import { useEquipmentCategories } from "@/common/hooks/queries/useEquipmentCategories";
import { Mapper } from "@/common/services/mapper";
import { getYearOptions } from "@/common/utils/getYearOptions";
import { useEquipmentModels } from "@/features/equipmentModels/hooks/useEquipmentModels";
import { EquipmentModelsTransformer } from "@/features/equipmentModels/services/equipmentModelsTransformer";
import { EquipmentCategoryData } from "@/features/equipmentModels/types/equipmentCategories";
import { EquipmentModelData, EquipmentModelTableItem } from "@/features/equipmentModels/types/equipmentModels";
import { CarFront, Search } from "lucide-react";
import { Tooltip } from 'react-tooltip';
import { defaultColumns } from "../../constants/columns.constant";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import useDebounce from "@/common/hooks/useDebounce";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";

const EquipmentModels = () => {
  const { filterParams, setFilterParams } = useFilterParams();

  const [search, setSearch] = useDebounce(filterParams.search || '');
  const [searchEquipmentCategory, setSearchEquipmentCategory] = useDebounce('');

  const {
    data: equipmentModels,
    meta,
    isPending: isPendingEquipmentModel,
    isLoading: isLoadingEquipmentModel,
    error: equipmentModelsError = {} as any,
    refetch: refetchEquipmentModels
  } = useEquipmentModels({
    filters: {
      search: search,
      page: filterParams.page || 1,
      category: filterParams.category?.value,
      year: filterParams.year?.value,
    },
    mapper: Mapper.mapWithTransformer<EquipmentModelData[], EquipmentModelTableItem[]>({
      transformer: EquipmentModelsTransformer.tableTransformer('Admin::EquipmentModels::Show')
    })
  });

  const { data: equipmentCategoryOptions } = useEquipmentCategories({
    filters: { search: searchEquipmentCategory },
    mapper: Mapper.mapToOptions<EquipmentCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  });

  return (
    <div>
      <div className="flex flex-col gap-6">
        <ListHeader
          Icon={<CarFront className="w-10 h-10" strokeWidth={1.5} />}
          title="Modelos de equipamento"
          createButtonText="Novo Modelo de Equipamento"
          operationKlassToCheckPermission="Admin::EquipmentModels::Create"
          dataTestid="create_equipment_models_button"
          disableCreateButton={isPendingEquipmentModel || equipmentModelsError?.status === 403}
        />

        <ErrorMessageWrapper
          className="h-[calc(100vh-128px)]"
          statusCode={equipmentModelsError?.status}
          refetchFunction={refetchEquipmentModels}
        >
          <div className="h-[calc(100vh-128px)] flex flex-col gap-6">
            <div className="flex flex-col gap-4">
              <TextField
                LeftIcon={<Search />}
                placeholder="Pesquisar"
                className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
                onChange={(e) => {
                  setSearch((e.target as HTMLInputElement).value);
                  setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }))
                }}
                value={filterParams.search}
              />
              <ListFilters
                filters={[
                  {
                    className: 'w-[196px]',
                    onChange: (e) => setFilterParams((prev) => ({ ...prev, category: e })),
                    onInputChange: setSearchEquipmentCategory,
                    value: filterParams.category,
                    options: [{ label: 'Todas', value: '' }, ...(equipmentCategoryOptions || [])],
                    placeholder: 'Categorias'
                  },
                  {
                    className: 'w-[160px]',
                    onChange: (e) => setFilterParams((prev) => ({ ...prev, year: e })),
                    value: filterParams.year,
                    options: [{ label: 'Todas', value: '' }, ...(getYearOptions(1900))],
                    placeholder: 'Ano'
                  },
                ]}
              />
            </div>

            <Table
              columns={defaultColumns}
              isLoadingData={isLoadingEquipmentModel}
              page={filterParams.page || 1}
              rows={equipmentModels}
              totalPages={meta.total_pages}
              totalCount={meta.total}
              hasPagination
              setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
            />

            <div className="z-50">
              {equipmentModels.map((equipmentModel) => (
                <Tooltip id={equipmentModel.id} key={equipmentModel.id}>
                  {equipmentModel.applicable_products?.map((product) => product.display_name).join(', ')}
                </Tooltip>
              ))}
            </div>
          </div>
        </ErrorMessageWrapper>
      </div>
    </div>
  );
};

export default EquipmentModels;
